import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type { SubscriptionAccessChange } from 'lumen/components/settings/users/modals/modal-subscription-access';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import isBadRequestError from 'lumen/utils/is-bad-request-error';

const SORT_KEY_MAP: Record<string, string> = {
  createdAt: 'date',
  subscriptionName: 'name',
};

export interface RelationshipsPage {
  relationships: Userhotelrelationship[];
  totalResults: number;
}

export interface LoadRelationshipsParams {
  page: number;
  searchQuery?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  userId: string;
}

export interface BulkDeletePayload {
  exclude: boolean;
  q?: string;
  subscription_ids: number[];
  user_id: string;
}

/**
 * Holds the data loading and mutation logic behind the "view subscription
 * access" modal, shared by every context (hotel, market, ...) it's rendered
 * in.
 */
export default class SettingsUserSubscriptionAccessService extends Service {
  @service declare access: Services['access'];
  @service('ajax') declare ajaxService: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  loadRelationshipsTask = task(
    { restartable: true },
    async (
      params: LoadRelationshipsParams,
    ): Promise<RelationshipsPage | undefined> => {
      try {
        const query: Record<string, string | number> = {
          user_id: params.userId,
          page: params.page,
        };

        if (params.searchQuery) {
          query['q'] = params.searchQuery;
        }

        if (params.sortBy) {
          query['sort_by'] = SORT_KEY_MAP[params.sortBy] ?? params.sortBy;
          query['sort_order'] = params.sortDirection ?? 'asc';
        }

        if (params.page === 0) {
          this.store.unloadAll('userhotelrelationship');
        }

        const results = await this.store.query<Userhotelrelationship>(
          'userhotelrelationship',
          query,
        );
        const meta = results.meta as { results_count: number };

        return {
          relationships: results.slice(),
          totalResults: meta.results_count,
        };
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );

  async addSubscriptionAccess(
    userId: string,
    subscriptionIds: number[],
    exclude = false,
  ): Promise<boolean> {
    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'POST',
        data: {
          // In exclude mode the ids are the subscriptions to leave out, so the
          // flag is only sent when set to keep the default payload unchanged.
          ...(exclude && { exclude }),
          subscription_ids: subscriptionIds,
          user_id: userId,
        },
      });

      this.toast.success(
        this.intl.t('settings.users.add_subscription.success'),
        { showIcon: true },
      );
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      // The backend rejects select all (exclude mode) for staff, who don't
      // administer a set of subscriptions.
      const isStaffSelectAll =
        exclude && this.access.isStaffUser && isBadRequestError(error);

      this.toast.error(
        this.intl.t(
          isStaffSelectAll
            ? 'settings.users.select_all_staff_error'
            : 'settings.users.add_subscription.failure',
        ),
        { showIcon: true },
      );
      return false;
    }
  }

  async bulkDelete(payload: BulkDeletePayload): Promise<boolean> {
    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'DELETE',
        data: payload,
      });

      this.toast.success(this.intl.t('settings.users.bulk.delete_success'), {
        showIcon: true,
      });
      return true;
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.bulk.delete_failure'), {
        showIcon: true,
      });
      return false;
    }
  }

  async deleteRelationship(
    userId: string,
    userEmail: string,
    subscriptionId: number,
    subscriptionName: string,
  ): Promise<boolean> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      text: this.intl.t('settings.users.subscription_access.confirm_delete', {
        userName: userEmail,
        subscriptionName,
      }),
      title: this.intl.t(
        'settings.users.subscription_access.remove_subscription',
      ),
    });

    if (!confirmed) {
      return false;
    }

    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'DELETE',
        data: {
          subscription_ids: [subscriptionId],
          user_id: userId,
        },
      });

      const record = (
        this.store.peekAll(
          'userhotelrelationship',
        ) as unknown as Userhotelrelationship[]
      ).find((relationship) => relationship.subscriptionId === subscriptionId);

      if (record) {
        this.store.unloadRecord(record);
      }

      this.toast.success(
        this.intl.t('settings.users.subscription_access.delete_success'),
        { showIcon: true },
      );
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.subscription_access.delete_failure'),
        { showIcon: true },
      );
      return false;
    }
  }

  isOwnUser(userId: string): boolean {
    return userId === this.hotelAndMarketService.user?.id;
  }

  async saveChanges(
    userId: string,
    changes: SubscriptionAccessChange[],
  ): Promise<boolean> {
    if (this.isOwnUser(userId)) {
      const confirmed = await this.alerting.showConfirmWarningAlert({
        text: this.intl.t('settings.hotel_access.confirm_edit_self'),
      });

      if (!confirmed) {
        return false;
      }
    }

    const changedSubscriptionCount = changes.reduce(
      (total, change) => total + change.subscriptionIds.length,
      0,
    );

    try {
      for (const change of changes) {
        await this.ajaxService.request('userhotelrelationships/', {
          method: 'PUT',
          data: {
            subscription_ids: change.subscriptionIds,
            user_id: change.userId,
            ...(change.accessRight && { access_right: change.accessRight }),
            ...(change.accessibleProducts && {
              accessible_products: change.accessibleProducts,
            }),
          },
        });
      }

      this.toast.success(
        this.intl.t('settings.users.edit.success', {
          count: changedSubscriptionCount,
        }),
        { showIcon: true },
      );
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.edit.failure', {
          count: changedSubscriptionCount,
        }),
        { showIcon: true },
      );
      return false;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'settings-user-subscription-access': SettingsUserSubscriptionAccessService;
  }
}
